import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import ApplyModal from '../components/ApplyModal';
import { 
  MapPin, Briefcase, DollarSign, Clock, ArrowLeft, 
  CheckCircle, Building, Mail, Phone, User, Share2, Check
} from 'lucide-react';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [existingApplication, setExistingApplication] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const orgSlug = position?.organizations?.slug || '';
    const url = orgSlug ? `${window.location.origin}/${orgSlug}/jobs/${id}` : window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    fetchPositionDetails();
  }, [id, userProfile]);

  const fetchPositionDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/positions/${id}`);
      
      if (response.data.success) {
        setPosition(response.data.data);
        
        // Check if user has already applied
        if (userProfile?.id) {
          await checkExistingApplication();
        }
      }
    } catch (error) {
      console.error('Error fetching position:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingApplication = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/${id}/check-application/${userProfile.id}`
      );
      
      if (response.data.success && response.data.data) {
        setExistingApplication(response.data.data);
      }
    } catch (error) {
      console.error('Error checking application:', error);
    }
  };

  const handleApplySuccess = (applicationData) => {
    setExistingApplication(applicationData);
    setShowApplyModal(false);
    checkExistingApplication();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Loading position details...</p>
        </div>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Position not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/jobs')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Jobs
        </button>

        {/* Position Header */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-10 h-10 text-white" />
            </div>
            
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {position.title}
              </h1>
              <p className="text-xl text-gray-700 mb-4">
                {position.company_name || position.organizations?.name}
              </p>
              
              {/* Quick Info */}
              <div className="flex flex-wrap gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{position.location || 'Location not specified'}</span>
                </div>
                
                {position.employment_type && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span className="capitalize">{position.employment_type}</span>
                  </div>
                )}

                {position.workplace && (
                  <div className="flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    <span className="capitalize">{position.workplace}</span>
                  </div>
                )}

                {(position.salary_min || position.salary_max) && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    <span>
                      {position.salary_currency || '€'} {position.salary_min?.toLocaleString()}
                      {position.salary_max && ` - ${position.salary_max.toLocaleString()}`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Apply Button */}
            <div className="flex-shrink-0">
              {/* Application Status / Apply Button */}
              {existingApplication ? (
                existingApplication.status === 'withdrawn' ? (
                  // Allow reapplication if withdrawn
                  <button
                    onClick={() => setShowApplyModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-md font-medium transition-colors"
                    data-testid="apply-again-btn"
                  >
                    Apply Again
                  </button>
                ) : (
                  // Show application status for all other statuses
                  <div className="space-y-2">
                    <button
                      disabled
                      className="bg-green-100 text-green-700 px-8 py-3 rounded-md font-medium flex items-center gap-2 cursor-not-allowed"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Applied
                    </button>
                    <p className="text-sm text-gray-600">
                      Status: <span className="font-semibold capitalize">{existingApplication.status}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Applied on {new Date(existingApplication.applied_at).toLocaleDateString()}
                    </p>
                  </div>
                )
              ) : (
                // Show apply button if not applied yet
                <button
                  onClick={() => setShowApplyModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-md font-medium transition-colors"
                  data-testid="apply-now-btn"
                >
                  Apply Now
                </button>
              )}
              <button
                onClick={handleShare}
                className="px-4 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium inline-flex items-center gap-2"
                data-testid="share-job-btn"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Share'}
              </button>
            </div>
          </div>

          {/* Skills */}
          {position.must_have_skills && position.must_have_skills.length > 0 && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Required Skills</h3>
              <div className="flex flex-wrap gap-2">
                {position.must_have_skills.map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {position.nice_to_have_skills && position.nice_to_have_skills.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Nice to Have</h3>
              <div className="flex flex-wrap gap-2">
                {position.nice_to_have_skills.map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Languages */}
          {position.languages && position.languages.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Required Languages</h3>
              <div className="flex flex-wrap gap-2">
                {position.languages.map((lang, idx) => (
                  <span key={idx} className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                    {lang}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Apply Modal */}
        {showApplyModal && position && userProfile && (
          <ApplyModal
            position={position}
            userProfile={userProfile}
            onClose={() => setShowApplyModal(false)}
            onSuccess={handleApplySuccess}
          />
        )}

        {/* Job Description */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-testid="job-description-heading">Job Description</h2>
          
          {position.description && position.description !== '<p><br></p>' && (
            <div className="mb-6" data-testid="job-description-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">About the Role</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.description }} />
            </div>
          )}

          {position.responsibilities && position.responsibilities !== '<p><br></p>' && (
            <div className="mb-6" data-testid="job-responsibilities-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Responsibilities</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.responsibilities }} />
            </div>
          )}

          {position.profile_requirements && position.profile_requirements !== '<p><br></p>' && (
            <div className="mb-6" data-testid="job-profile-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Profile & Requirements</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.profile_requirements }} />
            </div>
          )}

          {position.benefits && position.benefits !== '<p><br></p>' && (
            <div className="mb-6" data-testid="job-benefits-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Benefits</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.benefits }} />
            </div>
          )}

          {/* Legacy requirements field for older positions */}
          {position.requirements && position.requirements !== '<p><br></p>' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.requirements }} />
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {position.contact_name && (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Contact Person</p>
                  <p className="font-medium text-gray-900">{position.contact_name}</p>
                  {position.contact_title && (
                    <p className="text-sm text-gray-600">{position.contact_title}</p>
                  )}
                </div>
              </div>
            )}

            {position.company_email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-blue-600">{position.company_email}</p>
                </div>
              </div>
            )}

            {position.contact_phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{position.contact_phone}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
